import React, { useState, useEffect } from 'react';
import { 
  Plus, ClipboardList, Trash2, Edit2, X, Save, 
  Loader2, ChevronRight, Hash, Type, CheckSquare, 
  MessageSquare, Briefcase, Layout, Layers, Info,
  AlertCircle, ArrowUp, ArrowDown, List, Settings, 
  BookOpen, Search, Pin, ChevronLeft, ArrowLeft,
  GripVertical, MousePointer2, ExternalLink,
  PanelLeftClose, PanelLeft, PanelRightClose, PanelRight,
  ChevronDown, MousePointer, Home, Building, Zap, 
  Droplets, Wind, Thermometer, Shield, Construction, 
  Wrench, Hammer, Lightbulb, Power, Waves, Flame, 
  Bug, Camera, Box, Scaling, MapPin, Link2, Unlink
} from 'lucide-react';
import { ReportTemplate, Service, ReportSection, ReportItem, ReportElement, ReportElementType, InspectionNarrative } from '../types';
import { supabase, toSnakeCase, getErrorMessage, toCamelCase } from '../lib/supabase';

// Technical library of icons for selection
const AVAILABLE_ICONS: Record<string, any> = {
  'Home': Home,
  'Building': Building,
  'Zap': Zap,
  'Droplets': Droplets,
  'Wind': Wind,
  'Thermometer': Thermometer,
  'Shield': Shield,
  'Construction': Construction,
  'Wrench': Wrench,
  'Hammer': Hammer,
  'Search': Search,
  'Lightbulb': Lightbulb,
  'Power': Power,
  'Waves': Waves,
  'Flame': Flame,
  'Bug': Bug,
  'Camera': Camera,
  'Box': Box,
  'Scaling': Scaling,
  'MapPin': MapPin,
  'Layout': Layout,
  'Layers': Layers,
  'Info': Info,
  'ClipboardList': ClipboardList
};

const IconRenderer = ({ name, size = 16, className = "" }: { name?: string, size?: number, className?: string }) => {
  const IconComponent = name && AVAILABLE_ICONS[name] ? AVAILABLE_ICONS[name] : (AVAILABLE_ICONS['Info']);
  return <IconComponent size={size} className={className} />;
};

const IconPicker = ({ selected, onSelect }: { selected?: string, onSelect: (name: string) => void }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button 
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="p-3 bg-white border border-slate-200 rounded-xl hover:border-[#606c38] transition-all flex items-center gap-2 group"
      >
        <IconRenderer name={selected} size={18} className="text-[#606c38]" />
        <span className="text-[10px] font-black uppercase text-slate-400 group-hover:text-slate-600">Choose Icon</span>
        <ChevronDown size={12} className={`text-slate-300 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-[150]" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full left-0 mt-2 p-4 bg-white border border-slate-200 rounded-2xl shadow-2xl z-[160] grid grid-cols-6 gap-2 w-[280px] animate-in zoom-in-95">
            {Object.keys(AVAILABLE_ICONS).map((iconName) => (
              <button
                key={iconName}
                type="button"
                onClick={() => { onSelect(iconName); setIsOpen(false); }}
                className={`p-2 rounded-lg hover:bg-slate-50 transition-all flex items-center justify-center ${selected === iconName ? 'bg-slate-100 ring-2 ring-[#606c38]' : ''}`}
                title={iconName}
              >
                <IconRenderer name={iconName} size={20} className={selected === iconName ? 'text-[#606c38]' : 'text-slate-400'} />
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

interface ReportTemplatesProps {
  templates: ReportTemplate[];
  setTemplates: React.Dispatch<React.SetStateAction<ReportTemplate[]>>;
  services: Service[];
}

const ReportTemplates: React.FC<ReportTemplatesProps> = ({ templates, setTemplates, services }) => {
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [narratives, setNarratives] = useState<InspectionNarrative[]>([]);
  
  // Builder UI State
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const [showNarrativeSearch, setShowNarrativeSearch] = useState<{ elementId: string, optionValue: string } | null>(null);
  const [narSearchQuery, setNarSearchQuery] = useState('');
  
  // Collapsible Sidebar States
  const [isFlowCollapsed, setIsFlowCollapsed] = useState(false);
  const [isItemsCollapsed, setIsItemsCollapsed] = useState(false);

  const [formData, setFormData] = useState<Partial<ReportTemplate>>({
    name: '',
    serviceId: '',
    structure: []
  });

  const fetchNarratives = async () => {
    const { data } = await supabase.from('inspection_narratives').select('*');
    if (data) setNarratives(data.map(toCamelCase));
  };

  const handleOpenBuilder = async (t?: ReportTemplate) => {
    await fetchNarratives();
    if (t) {
      setEditingId(t.id);
      setFormData(t);
      if (t.structure.length > 0) {
        setActiveSectionId(t.structure[0].id);
        if (t.structure[0].items.length > 0) {
          setActiveItemId(t.structure[0].items[0].id);
        }
      }
    } else {
      setEditingId(null);
      const newId = crypto.randomUUID();
      setFormData({ 
        name: 'New Blueprint', 
        serviceId: services[0]?.id || '', 
        structure: [{ id: newId, name: 'General Overview', items: [], internalInstructions: '', icon: 'Layout' }] 
      });
      setActiveSectionId(newId);
      setActiveItemId(null);
    }
    setIsBuilderOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name) return;
    setSaving(true);
    try {
      const payload = toSnakeCase({ ...formData });
      if (editingId) {
        const { error } = await supabase.from('report_templates').update(payload).eq('id', editingId);
        if (error) throw error;
        setTemplates(prev => prev.map(t => t.id === editingId ? { ...t, ...formData } as ReportTemplate : t));
      } else {
        const { data, error } = await supabase.from('report_templates').insert([payload]).select().single();
        if (error) throw error;
        setTemplates(prev => [toCamelCase(data), ...prev]);
      }
      setIsBuilderOpen(false);
    } catch (err) { alert(getErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const addSection = () => {
    const newSection: ReportSection = { id: crypto.randomUUID(), name: 'New Section', items: [], internalInstructions: '', icon: 'Layout' };
    setFormData(prev => ({ ...prev, structure: [...(prev.structure || []), newSection] }));
    setActiveSectionId(newSection.id);
    setActiveItemId(null);
    if (isFlowCollapsed) setIsFlowCollapsed(false);
  };

  const moveSection = (index: number, direction: 'up' | 'down') => {
    const newStructure = [...(formData.structure || [])];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newStructure.length) return;
    [newStructure[index], newStructure[targetIndex]] = [newStructure[targetIndex], newStructure[index]];
    setFormData(prev => ({ ...prev, structure: newStructure }));
  };

  const addItem = () => {
    if (!activeSectionId) return;
    const newItem: ReportItem = { id: crypto.randomUUID(), name: 'New Technical Item', elements: [], quickNarrativeIds: [], icon: 'Layers' };
    setFormData(prev => ({
      ...prev,
      structure: prev.structure?.map(s => s.id === activeSectionId ? { ...s, items: [...s.items, newItem] } : s)
    }));
    setActiveItemId(newItem.id);
    if (isItemsCollapsed) setIsItemsCollapsed(false);
  };

  const moveItem = (index: number, direction: 'up' | 'down') => {
    if (!activeSectionId) return;
    setFormData(prev => ({
      ...prev,
      structure: prev.structure?.map(s => {
        if (s.id !== activeSectionId) return s;
        const newItems = [...s.items];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= newItems.length) return s;
        [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
        return { ...s, items: newItems };
      })
    }));
  };

  const addElement = (type: ReportElementType) => {
    if (!activeSectionId || !activeItemId) return;
    const newElement: ReportElement = { 
      id: crypto.randomUUID(), 
      type, 
      label: `New ${type} Field`, 
      required: true,
      options: (type === 'dropdown' || type === 'multiselect') ? ['Option 1'] : undefined,
      optionNarratives: {}
    };
    setFormData(prev => ({
      ...prev,
      structure: prev.structure?.map(s => s.id === activeSectionId ? {
        ...s,
        items: s.items.map(i => i.id === activeItemId ? { ...i, elements: [...i.elements, newElement] } : i)
      } : s)
    }));
  };

  const moveElement = (index: number, direction: 'up' | 'down') => {
    if (!activeSectionId || !activeItemId) return;
    setFormData(prev => ({
      ...prev,
      structure: prev.structure?.map(s => {
        if (s.id !== activeSectionId) return s;
        return {
          ...s,
          items: s.items.map(i => {
            if (i.id !== activeItemId) return i;
            const newElements = [...i.elements];
            const targetIndex = direction === 'up' ? index - 1 : index + 1;
            if (targetIndex < 0 || targetIndex >= newElements.length) return i;
            [newElements[index], newElements[targetIndex]] = [newElements[targetIndex], newElements[index]];
            return { ...i, elements: newElements };
          })
        };
      })
    }));
  };

  const updateElement = (elementId: string, updates: Partial<ReportElement>) => {
    setFormData(prev => ({
      ...prev,
      structure: prev.structure?.map(s => s.id === activeSectionId ? {
        ...s,
        items: s.items.map(i => i.id === activeItemId ? {
          ...i,
          elements: i.elements.map(e => e.id === elementId ? { ...e, ...updates } : e)
        } : i)
      } : s)
    }));
  };

  const linkNarrativeToOption = (narId: string) => {
    if (!showNarrativeSearch) return;
    const { elementId, optionValue } = showNarrativeSearch;
    
    setFormData(prev => ({
      ...prev,
      structure: prev.structure?.map(s => s.id === activeSectionId ? {
        ...s,
        items: s.items.map(i => i.id === activeItemId ? {
          ...i,
          elements: i.elements.map(el => {
            if (el.id !== elementId) return el;
            return {
              ...el,
              optionNarratives: {
                ...(el.optionNarratives || {}),
                [optionValue]: narId
              }
            };
          })
        } : i)
      } : s)
    }));
    setShowNarrativeSearch(null);
  };

  const unlinkNarrativeFromOption = (elementId: string, optionValue: string) => {
    setFormData(prev => ({
      ...prev,
      structure: prev.structure?.map(s => s.id === activeSectionId ? {
        ...s,
        items: s.items.map(i => i.id === activeItemId ? {
          ...i,
          elements: i.elements.map(el => {
            if (el.id !== elementId) return el;
            const nextMap = { ...(el.optionNarratives || {}) };
            delete nextMap[optionValue];
            return { ...el, optionNarratives: nextMap };
          })
        } : i)
      } : s)
    }));
  };

  const activeSection = formData.structure?.find(s => s.id === activeSectionId);
  const activeItem = activeSection?.items.find(i => i.id === activeItemId);

  if (isBuilderOpen) {
    return (
      <div className="fixed inset-0 z-[600] bg-[#f8faf7] flex flex-col animate-in fade-in duration-500">
        {/* Builder Header */}
        <header className="h-20 bg-white border-b border-slate-200 px-6 flex items-center justify-between shrink-0 z-[110] shadow-sm">
          <div className="flex items-center gap-6">
            <button onClick={() => setIsBuilderOpen(false)} className="p-2.5 hover:bg-slate-50 rounded-xl text-slate-400 transition-all border border-transparent hover:border-slate-100">
              <ArrowLeft size={20} />
            </button>
            <div className="h-10 w-px bg-slate-100" />
            <div className="space-y-1">
              <input 
                className="bg-transparent border-none outline-none font-black text-slate-900 text-lg uppercase tracking-tighter focus:ring-0 w-48 sm:w-80"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Blueprint Title..."
              />
              <div className="flex items-center gap-4">
                <select 
                  className="bg-transparent border-none p-0 text-[10px] font-black text-[#606c38] uppercase tracking-widest outline-none cursor-pointer"
                  value={formData.serviceId}
                  onChange={(e) => setFormData({...formData, serviceId: e.target.value})}
                >
                  <option value="">Global Protocol</option>
                  {services.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <span className="text-[9px] font-bold text-slate-300 uppercase tracking-widest hidden sm:inline">• Studio Mode v3.1 (Logic-Enabled)</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setIsBuilderOpen(false)} className="px-6 py-2.5 text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-slate-600 hidden sm:block">Discard</button>
            <button disabled={saving} onClick={handleSave} className="px-6 sm:px-8 py-3 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-xl hover:bg-slate-800 transition-all">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} <span className="hidden sm:inline">Commit Architecture</span><span className="sm:hidden">Save</span>
            </button>
          </div>
        </header>

        <div className="flex-1 flex overflow-hidden">
          {/* Pane 1: Flow Sections */}
          <aside className={`${isFlowCollapsed ? 'w-16' : 'w-80'} bg-white border-r border-slate-200 flex flex-col shrink-0 transition-all duration-300 z-[100]`}>
            <div className="p-4 border-b border-slate-50 flex items-center justify-between h-14">
              {!isFlowCollapsed ? (
                <>
                  <div className="flex items-center gap-2 text-[#606c38]">
                    <Layout size={14} />
                    <span className="text-[10px] font-black uppercase tracking-[0.2em]">Audit Flow</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={addSection} className="p-1.5 bg-slate-50 text-slate-400 hover:text-[#606c38] rounded-lg transition-colors"><Plus size={14} /></button>
                    <button onClick={() => setIsFlowCollapsed(true)} className="p-1.5 text-slate-300 hover:text-slate-600"><PanelLeftClose size={16} /></button>
                  </div>
                </>
              ) : (
                <button onClick={() => setIsFlowCollapsed(false)} className="w-full flex items-center justify-center p-2 text-[#606c38] hover:bg-slate-50 rounded-lg transition-colors">
                  <PanelLeft size={18} />
                </button>
              )}
            </div>
            <nav className="flex-1 overflow-y-auto no-scrollbar p-2 space-y-1">
              {formData.structure?.map((s, idx) => (
                <div key={s.id} className="group relative">
                  <button 
                    onClick={() => { setActiveSectionId(s.id); setActiveItemId(s.items[0]?.id || null); }}
                    title={s.name}
                    className={`w-full text-left p-3.5 rounded-xl transition-all flex items-center justify-between ${activeSectionId === s.id ? 'bg-[#606c38] text-white shadow-lg' : 'text-slate-400 hover:bg-slate-50'}`}
                  >
                    {!isFlowCollapsed ? (
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`p-1.5 rounded-lg ${activeSectionId === s.id ? 'bg-white/20' : 'bg-slate-100 text-slate-400'}`}>
                          <IconRenderer name={s.icon} size={14} />
                        </div>
                        <span className="text-xs font-bold uppercase tracking-tight truncate pr-4">{s.name}</span>
                      </div>
                    ) : (
                      <div className="w-full text-center">
                        <IconRenderer name={s.icon} size={18} className={activeSectionId === s.id ? 'text-white mx-auto' : 'text-slate-400 mx-auto'} />
                      </div>
                    )}
                    {!isFlowCollapsed && <ChevronRight size={14} className={activeSectionId === s.id ? 'opacity-100' : 'opacity-0'} />}
                  </button>
                  {!isFlowCollapsed && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="flex flex-col">
                        <button onClick={(e) => { e.stopPropagation(); moveSection(idx, 'up'); }} disabled={idx === 0} className="p-1 text-white/50 hover:text-white disabled:opacity-0"><ArrowUp size={10} /></button>
                        <button onClick={(e) => { e.stopPropagation(); moveSection(idx, 'down'); }} disabled={idx === (formData.structure?.length || 0) - 1} className="p-1 text-white/50 hover:text-white disabled:opacity-0"><ArrowDown size={10} /></button>
                      </div>
                      <button onClick={() => setFormData(prev => ({...prev, structure: prev.structure?.filter(sec => sec.id !== s.id)}))} className="p-1.5 text-white/50 hover:text-white"><Trash2 size={12} /></button>
                    </div>
                  )}
                </div>
              ))}
            </nav>
          </aside>

          {/* Pane 2: Items in selected section */}
          <aside className={`${isItemsCollapsed ? 'w-16' : 'w-[400px]'} bg-slate-50/50 border-r border-slate-200 flex flex-col shrink-0 transition-all duration-300 z-[90]`}>
            <div className="p-4 border-b border-slate-200 flex items-center justify-between h-14 overflow-hidden">
              {!isItemsCollapsed ? (
                <>
                  <div className="flex items-center gap-2 text-slate-400 min-w-0">
                    <Layers size={14} className="shrink-0" />
                    <span className="text-[10px] font-black uppercase tracking-[0.2em] truncate">Technical Items</span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={addItem} className="px-3 py-1 bg-[#606c38] text-white rounded-lg text-[9px] font-black uppercase tracking-widest shadow-sm">Add</button>
                    <button onClick={() => setIsItemsCollapsed(true)} className="p-1.5 text-slate-300 hover:text-slate-600"><PanelLeftClose size={16} /></button>
                  </div>
                </>
              ) : (
                <button onClick={() => setIsItemsCollapsed(false)} className="w-full flex items-center justify-center p-2 text-slate-400 hover:bg-white/50 rounded-lg transition-colors">
                  <PanelLeft size={18} />
                </button>
              )}
            </div>
            <div className="flex-1 overflow-y-auto no-scrollbar p-2 space-y-2">
              {activeSection ? (
                <>
                  {!isItemsCollapsed && (
                    <div className="p-4 mb-4 bg-white border border-slate-200 rounded-2xl space-y-4 shadow-sm animate-in fade-in zoom-in-95">
                      <div className="flex items-center justify-between">
                         <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Section Identity</label>
                         <IconPicker selected={activeSection.icon} onSelect={(icon) => setFormData(prev => ({...prev, structure: prev.structure?.map(s => s.id === activeSectionId ? {...s, icon} : s)}))} />
                      </div>
                      <input className="w-full bg-transparent border-none outline-none font-bold text-sm text-slate-800 focus:ring-0 p-0" value={activeSection.name} onChange={(e) => setFormData(prev => ({...prev, structure: prev.structure?.map(s => s.id === activeSectionId ? {...s, name: e.target.value} : s)}))} />
                      <div className="h-px bg-slate-50" />
                      <textarea rows={3} className="w-full bg-transparent border-none outline-none text-xs text-slate-500 font-medium resize-none focus:ring-0 p-0" placeholder="Internal technician guidance..." value={activeSection.internalInstructions || ''} onChange={(e) => setFormData(prev => ({...prev, structure: prev.structure?.map(s => s.id === activeSectionId ? {...s, internalInstructions: e.target.value} : s)}))} />
                    </div>
                  )}

                  {activeSection.items.map((item, idx) => (
                    <div key={item.id} className="group relative">
                      <button onClick={() => setActiveItemId(item.id)} title={item.name} className={`w-full text-left p-3.5 rounded-xl border transition-all flex items-center justify-between ${activeItemId === item.id ? 'bg-white border-2 border-[#606c38] text-[#606c38] shadow-md' : 'bg-white border-slate-200 text-slate-600 hover:border-[#606c38]/30'}`}>
                        {!isItemsCollapsed ? (
                          <>
                            <div className="flex items-center gap-3 min-w-0">
                               <IconRenderer name={item.icon} size={14} className={activeItemId === item.id ? 'text-[#606c38]' : 'text-slate-300'} />
                               <span className="text-[11px] font-bold uppercase tracking-tight truncate pr-4">{item.name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-[9px] font-black text-slate-300">{item.elements.length}</span>
                              <MousePointer2 size={12} className={activeItemId === item.id ? 'opacity-100' : 'opacity-0'} />
                            </div>
                          </>
                        ) : (
                          <div className="w-full text-center">
                            <IconRenderer name={item.icon} size={16} className={activeItemId === item.id ? 'text-[#606c38] mx-auto' : 'text-slate-300 mx-auto'} />
                          </div>
                        )}
                      </button>
                      {!isItemsCollapsed && (
                        <div className="absolute -right-1 top-1/2 -translate-y-1/2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all z-10">
                           <div className="flex flex-col">
                              <button onClick={(e) => { e.stopPropagation(); moveItem(idx, 'up'); }} disabled={idx === 0} className="p-1 bg-white border border-slate-100 rounded text-slate-400 hover:text-[#606c38] disabled:opacity-0"><ArrowUp size={10} /></button>
                              <button onClick={(e) => { e.stopPropagation(); moveItem(idx, 'down'); }} disabled={idx === activeSection.items.length - 1} className="p-1 bg-white border border-slate-100 rounded text-slate-400 hover:text-[#606c38] disabled:opacity-0"><ArrowDown size={10} /></button>
                           </div>
                           <button onClick={() => setFormData(prev => ({...prev, structure: prev.structure?.map(s => s.id === activeSectionId ? {...s, items: s.items.filter(i => i.id !== item.id)} : s)}))} className="p-1.5 bg-rose-50 text-rose-500 rounded-lg shadow-sm border border-rose-100"><Trash2 size={12} /></button>
                        </div>
                      )}
                    </div>
                  ))}
                </>
              ) : (
                <div className="h-full flex items-center justify-center text-center p-6">
                  {!isItemsCollapsed && <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest leading-loose">Select a flow section</p>}
                </div>
              )}
            </div>
          </aside>

          {/* Pane 3: Main Editor Area */}
          <main className="flex-1 bg-white overflow-y-auto no-scrollbar relative z-[80]">
            {activeItem ? (
              <div className="max-w-6xl mx-auto p-6 sm:p-12 space-y-12 sm:space-y-16 animate-in fade-in slide-in-from-right-4 duration-500">
                <section className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2"><Layers size={14} className="text-[#606c38]" /> Item Architecture</h4>
                    <IconPicker selected={activeItem.icon} onSelect={(icon) => setFormData(prev => ({...prev, structure: prev.structure?.map(s => s.id === activeSectionId ? {...s, items: s.items.map(i => i.id === activeItemId ? {...i, icon} : i)} : s)}))} />
                  </div>
                  <div className="flex items-center gap-6">
                     <div className="p-4 bg-slate-50 rounded-[2rem] text-[#606c38] border border-slate-100 shadow-inner">
                        <IconRenderer name={activeItem.icon} size={48} />
                     </div>
                     <input className="flex-1 bg-transparent border-none outline-none font-black text-slate-900 text-2xl sm:text-4xl uppercase tracking-tighter focus:ring-0 p-0" value={activeItem.name} onChange={(e) => setFormData(prev => ({...prev, structure: prev.structure?.map(s => s.id === activeSectionId ? {...s, items: s.items.map(i => i.id === activeItemId ? {...i, name: e.target.value} : i)} : s)}))} />
                  </div>
                </section>

                <div className="space-y-10">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-100 pb-4 gap-4">
                    <h5 className="text-[10px] font-black text-slate-900 uppercase tracking-widest flex items-center gap-2"><CheckSquare size={14} className="text-blue-500" /> Data Capture Fields</h5>
                    <div className="flex flex-wrap gap-2">
                      {(['checkbox', 'text', 'number', 'dropdown', 'multiselect'] as ReportElementType[]).map(type => (
                        <button key={type} onClick={() => addElement(type)} className="px-3 py-1.5 bg-slate-50 hover:bg-slate-100 rounded-lg text-[9px] font-black uppercase tracking-widest text-slate-500 border border-slate-200 transition-all flex items-center gap-1.5">
                          <Plus size={10} /> {type}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-6">
                    {activeItem.elements.map((el, idx) => (
                      <div key={el.id} className="p-8 bg-slate-50 rounded-[2.5rem] border border-slate-100 space-y-6 group/el animate-in slide-in-from-top-2 shadow-sm">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                          <div className="flex items-center gap-4 flex-1">
                            <div className="p-3 bg-white rounded-2xl shadow-sm text-[#606c38] shrink-0 border border-slate-100">
                              {el.type === 'checkbox' && <CheckSquare size={18} />}
                              {el.type === 'text' && <Type size={18} />}
                              {el.type === 'number' && <Hash size={18} />}
                              {(el.type === 'dropdown' || el.type === 'multiselect') && <List size={18} />}
                            </div>
                            <input className="flex-1 bg-transparent border-none outline-none font-black text-lg text-slate-800 focus:ring-0 p-0 uppercase tracking-tight" value={el.label} onChange={(e) => updateElement(el.id, { label: e.target.value })} />
                          </div>
                          <div className="flex items-center gap-3 self-end sm:self-auto">
                            <div className="flex items-center bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                               <button onClick={() => moveElement(idx, 'up')} disabled={idx === 0} className="p-2 text-slate-400 hover:text-[#606c38] hover:bg-slate-50 disabled:opacity-20 transition-colors"><ArrowUp size={14} /></button>
                               <div className="w-px h-4 bg-slate-100" />
                               <button onClick={() => moveElement(idx, 'down')} disabled={idx === activeItem.elements.length - 1} className="p-2 text-slate-400 hover:text-[#606c38] hover:bg-slate-50 disabled:opacity-20 transition-colors"><ArrowDown size={14} /></button>
                            </div>
                            <label className="flex items-center gap-2 cursor-pointer bg-white px-3 py-1.5 rounded-xl border border-slate-200 shadow-sm">
                              <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-[#606c38] focus:ring-[#606c38]" checked={el.required} onChange={(e) => updateElement(el.id, { required: e.target.checked })} />
                              <span className="text-[9px] font-black text-slate-400 uppercase">Required</span>
                            </label>
                            <button onClick={() => setFormData(prev => ({...prev, structure: prev.structure?.map(s => s.id === activeSectionId ? {...s, items: s.items.map(i => i.id === activeItemId ? {...i, elements: i.elements.filter(e => e.id !== el.id)} : i)} : s)}))} className="p-2 text-slate-300 hover:text-rose-500 transition-colors"><Trash2 size={16} /></button>
                          </div>
                        </div>

                        {(el.type === 'dropdown' || el.type === 'multiselect') && (
                          <div className="pl-0 sm:pl-16 space-y-4">
                            <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">
                               <Link2 size={12} className="text-blue-500" /> Automation Protocol Logic
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              {el.options?.map((opt, oIdx) => {
                                const linkedNarId = el.optionNarratives?.[opt];
                                const linkedNar = narratives.find(n => n.id === linkedNarId);
                                return (
                                  <div key={oIdx} className="bg-white border border-slate-200 p-4 rounded-2xl shadow-sm space-y-3 group/opt">
                                    <div className="flex items-center gap-3">
                                      <input 
                                        className="flex-1 text-xs font-black text-slate-600 bg-transparent border-none outline-none focus:ring-0 p-0 uppercase tracking-tighter"
                                        value={opt}
                                        onChange={(e) => {
                                          const newOpts = [...(el.options || [])];
                                          newOpts[oIdx] = e.target.value;
                                          updateElement(el.id, { options: newOpts });
                                        }}
                                      />
                                      <button onClick={() => updateElement(el.id, { options: el.options?.filter((_, i) => i !== oIdx) })} className="text-slate-200 hover:text-rose-500 transition-colors"><X size={12} /></button>
                                    </div>
                                    
                                    {linkedNar ? (
                                       <div className="flex items-center justify-between p-2.5 bg-blue-50/50 border border-blue-100 rounded-xl animate-in zoom-in-95">
                                          <div className="flex items-center gap-2 min-w-0">
                                             <MessageSquare size={12} className="text-blue-500 shrink-0" />
                                             <span className="text-[10px] font-bold text-blue-700 truncate">{linkedNar.title}</span>
                                          </div>
                                          <button onClick={() => unlinkNarrativeFromOption(el.id, opt)} className="text-blue-300 hover:text-blue-600"><Unlink size={12} /></button>
                                       </div>
                                    ) : (
                                       <button 
                                          type="button" 
                                          onClick={() => setShowNarrativeSearch({ elementId: el.id, optionValue: opt })}
                                          className="w-full py-2 bg-slate-50 border border-slate-100 rounded-xl text-[9px] font-black uppercase tracking-widest text-slate-400 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-100 transition-all flex items-center justify-center gap-2"
                                       >
                                          <Link2 size={10} /> Link Narrative
                                       </button>
                                    )}
                                  </div>
                                );
                              })}
                              <button onClick={() => updateElement(el.id, { options: [...(el.options || []), 'New Option'] })} className="p-4 border-2 border-dashed border-slate-200 rounded-2xl text-[10px] font-black text-slate-300 hover:border-[#606c38] hover:text-[#606c38] transition-all flex items-center justify-center gap-2">+ Add Value</button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    {activeItem.elements.length === 0 && (
                      <div className="py-20 text-center border-2 border-dashed border-slate-100 rounded-[2.5rem] bg-slate-50/30">
                        <MousePointer2 size={32} className="mx-auto text-slate-200 mb-3" />
                        <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">No fields defined for this item</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center flex-col text-center p-10 sm:p-20 space-y-6">
                <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center text-slate-100">
                  <ClipboardList size={40} />
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-black text-slate-300 uppercase tracking-tighter">Editor Awaiting Target</h3>
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest max-w-xs mx-auto">Select a technical item from the registry list to architect its field logic.</p>
                </div>
              </div>
            )}
          </main>
        </div>

        {/* Narrative Catalog Modal */}
        {showNarrativeSearch && (
          <div className="fixed inset-0 z-[700] flex items-center justify-center p-6">
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={() => setShowNarrativeSearch(null)} />
            <div className="bg-white w-full max-w-2xl rounded-[2.5rem] shadow-2xl relative overflow-hidden flex flex-col max-h-[80vh] animate-in zoom-in-95 duration-300">
              <div className="p-6 sm:p-8 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-600 text-white rounded-2xl shadow-lg shadow-blue-600/20 hidden sm:block"><BookOpen size={20} /></div>
                  <div>
                    <h3 className="font-black text-slate-900 text-sm uppercase tracking-widest">Logic Selection Library</h3>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Link finding to option: "{showNarrativeSearch.optionValue}"</p>
                  </div>
                </div>
                <button onClick={() => setShowNarrativeSearch(null)} className="p-2 hover:bg-white rounded-full text-slate-400 transition-colors"><X size={24} /></button>
              </div>
              <div className="p-6 sm:p-8 space-y-6 flex-1 overflow-y-auto no-scrollbar">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder="Search technical narratives..." value={narSearchQuery} onChange={(e) => setNarSearchQuery(e.target.value)} autoFocus />
                </div>
                <div className="grid grid-cols-1 gap-3">
                  {narratives.filter(n => n.title.toLowerCase().includes(narSearchQuery.toLowerCase())).map(n => (
                    <button key={n.id} onClick={() => linkNarrativeToOption(n.id)} className="w-full text-left p-4 bg-white border border-slate-100 rounded-2xl hover:border-blue-500 hover:shadow-xl hover:shadow-blue-500/5 transition-all group flex items-center justify-between">
                      <div className="min-w-0 pr-6">
                        <h5 className="font-black text-slate-900 text-[11px] uppercase tracking-tight">{n.title}</h5>
                        <p className="text-[10px] text-slate-500 font-medium truncate mt-1">{n.content}</p>
                      </div>
                      <Link2 size={16} className="text-slate-200 group-hover:text-blue-500 transition-colors" />
                    </button>
                  ))}
                  {narratives.length === 0 && (
                    <div className="py-20 text-center space-y-4">
                      <MessageSquare size={40} className="mx-auto text-slate-100" />
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Registry catalog empty</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="animate-in fade-in duration-300">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h3 className="text-xl font-black text-slate-800 tracking-tight uppercase">Audit Architecture</h3>
          <p className="text-sm text-slate-500 font-medium">Build and manage technical blueprint protocols.</p>
        </div>
        <button onClick={() => handleOpenBuilder()} className="bg-slate-900 text-white px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg hover:bg-slate-800 transition-all flex items-center justify-center gap-2">
          <Plus size={18} /> New Architecture
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map(t => (
          <div key={t.id} className="bg-white p-6 rounded-2xl border border-slate-200 hover:border-slate-300 transition-all group flex flex-col justify-between shadow-sm hover:shadow-md">
            <div>
              <div className="flex justify-between items-start mb-4">
                <div className="p-3 bg-slate-50 text-slate-400 rounded-xl group-hover:bg-[#606c38]/10 group-hover:text-[#606c38] transition-colors">
                  <ClipboardList size={24} />
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => handleOpenBuilder(t)} className="p-2 text-slate-400 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition-all"><Edit2 size={16} /></button>
                  <button onClick={async () => { if(confirm('Purge blueprint protocol?')) { await supabase.from('report_templates').delete().eq('id', t.id); setTemplates(prev => prev.filter(i => i.id !== t.id)); }}} className="p-2 text-slate-400 hover:text-red-500 rounded-lg hover:bg-rose-50 transition-all"><Trash2 size={16} /></button>
                </div>
              </div>
              <h5 className="font-black text-slate-900 text-base uppercase tracking-tight leading-tight">{t.name}</h5>
              <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest mt-2">{services.find(s => s.id === t.serviceId)?.name || 'Global Registry Protocol'}</p>
            </div>
            <div className="mt-6 pt-6 border-t border-slate-50 flex items-center justify-between">
               <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t.structure.length} Technical Sections</span>
               <button onClick={() => handleOpenBuilder(t)} className="text-[#606c38] text-[9px] font-black uppercase tracking-widest flex items-center gap-1.5 hover:underline">Launch Studio <ChevronRight size={10} /></button>
            </div>
          </div>
        ))}
        {templates.length === 0 && (
          <div className="col-span-full py-32 text-center border-2 border-dashed border-slate-200 rounded-[2.5rem] bg-slate-50/50">
            <Layout size={64} className="mx-auto text-slate-200 mb-4" />
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">Protocol registry empty</p>
            <button onClick={() => handleOpenBuilder()} className="mt-6 px-8 py-3 bg-white border border-slate-200 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-900 hover:text-white transition-all shadow-sm">Initialize First Blueprint</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportTemplates;